import { NotificationManager } from 'Components/Notifications';
import { ErrorHandlerLogin } from 'Core/data/Helpers';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { isDataDogInitialized } from 'Core/datadog';

/* eslint-disable no-console */
/* eslint-disable no-unused-vars */

// DataDog status flags - now managed by the dedicated DataDog module
let isDataDogLogsInitialized = false;
let isDataDogRumInitialized = false;

// Check if DataDog RUM is initialized by the dedicated DataDog module
const checkDataDogStatus = () => {
  isDataDogRumInitialized = isDataDogInitialized();
  // Note: We assume logs are initialized if RUM is initialized
  isDataDogLogsInitialized = isDataDogRumInitialized;
};

// Check DataDog status on module load
checkDataDogStatus();

/**
 * Enhanced LogManager class with DataDog integration
 */
/**
 * LogManager class with static methods and DataDog integration
 */
export class LogManager {
  static logToDataDog(level, message, ...data) {
    // Re-check DataDog status before each operation
    checkDataDogStatus();
    
    if (isDataDogLogsInitialized) {
      try {
        const logData = {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          data: data.length === 1 ? data[0] : data
        };
        
        datadogLogs.logger[level](message, logData);
      } catch (error) {
        console.warn('Failed to log to DataDog:', error);
      }
    }
  }

  /**
   * Creates a RUM timing that can be used for performance measurements
   * @param {string} name - The name of the timing
   * @param {boolean} useView - If true, sends a one-off RUM timing from current view start.
   *                           If false, returns a RumTiming object for precise measuring.
   * @returns {RumTiming|void} RumTiming object or void if useView is true
   */
  static newRumTiming(name, useView = true) {
    if (!isDataDogRumInitialized) {
      console.warn('DataDog RUM not initialized, cannot create timing');
      return;
    }

    if (useView) {
      datadogRum.addTiming(name);
      return;
    }

    // Create a performance mark for precise timing
    const markName = `${name}_${Date.now()}`;
    try {
      performance.mark(markName);
      return {
        name: name,
        markName: markName,
        startTime: Date.now()
      };
    } catch (error) {
      console.warn('Failed to create performance mark:', error);
      return null;
    }
  }

  /**
   * Sends a RUM timing measurement and logs the duration
   * @param {RumTiming} timing - The timing object from newRumTiming
   * @param {object} context - Additional context to include with the timing
   */
  static sendRumTiming(timing, context = {}) {
    if (!isDataDogRumInitialized || !timing) {
      console.warn('DataDog RUM not initialized or invalid timing object');
      return;
    }

    const measureName = `${timing.name}_measure_${Date.now()}`;
    
    try {
      performance.measure(measureName, timing.markName);
      const measure = performance.getEntriesByName(measureName)[0];
      const duration = measure ? measure.duration : Date.now() - timing.startTime;
      
      // Add RUM action with timing and context
      datadogRum.addAction(timing.name, {
        [`${timing.name}_duration_ms`]: duration,
        timing_type: 'custom_performance',
        ...context
      });

      // Also log the timing for debugging
      this.info(`Custom timing: ${timing.name}`, {
        duration_ms: duration,
        ...context
      });

      // Clean up performance entries
      performance.clearMarks(timing.markName);
      performance.clearMeasures(measureName);
      
      return duration;
    } catch (error) {
      console.warn('Failed to send RUM timing:', error);
      // Fallback to simple duration calculation
      const fallbackDuration = Date.now() - timing.startTime;
      datadogRum.addAction(timing.name, {
        [`${timing.name}_duration_ms`]: fallbackDuration,
        timing_type: 'custom_performance_fallback',
        ...context
      });
      return fallbackDuration;
    }
  }

  /**
   * Adds a RUM action with context
   * @param {string} name - Action name
   * @param {object} context - Additional context
   */
  static addRumAction(name, context = {}) {
    if (isDataDogRumInitialized) {
      datadogRum.addAction(name, context);
    }
  }

  /**
   * Logs API request performance and response details
   * @param {string} endpoint - The API endpoint
   * @param {string} method - HTTP method
   * @param {number} duration - Request duration in ms
   * @param {string} status - Response status
   * @param {object} additionalContext - Additional context to log
   */
  static logApiPerformance(endpoint, method, duration, status, additionalContext = {}) {
    const context = {
      endpoint,
      method,
      duration_ms: duration,
      status,
      ...additionalContext
    };

    this.info(`API Request: ${method} ${endpoint}`, context);
    
    if (isDataDogRumInitialized) {
      datadogRum.addAction('api_request', context);
    }
  }

  static info(message, ...data) {
    console.log('Log => ', message, ...data);
    this.logToDataDog('info', message, ...data);
  }

  static error(message, ...data) {
    console.error('Error => ', message, ...data);
    this.logToDataDog('error', message, ...data);
  }

  static warn(message, ...data) {
    console.warn('Warning => ', message, ...data);
    this.logToDataDog('warn', message, ...data);
  }

  static debug(message, ...data) {
    console.debug('Debug => ', message, ...data);
    this.logToDataDog('debug', message, ...data);
  }

  static userRating(params) {
    const logData = {
      event: 'user_rating',
      ...params,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    console.log('User Rating:', logData);
    this.logToDataDog('info', 'User rating submitted', logData);
    return logData;
  }

  static getDataDogStatus() {
    checkDataDogStatus();
    return {
      logs: isDataDogLogsInitialized,
      rum: isDataDogRumInitialized
    };
  }
}

/**
 * RumTiming interface for TypeScript-like documentation
 * @typedef {Object} RumTiming
 * @property {string} name - The name of the timing
 * @property {string} markName - The performance mark name
 * @property {number} startTime - The start time in milliseconds
 */

let Exceptions;

export const ExceptionManager = (exc, module, method, show = false) => {
	/*if (!Exceptions)
				Exceptions = [];

				try
		Exceptions.push(exc)*/
	try {
		console.log(`Error => Module: ${module} \nMethod: ${method} \nexc:`, exc);

		// Log to DataDog
		LogManager.logToDataDog('error', `Exception in ${module}.${method}`, {
			module,
			method,
			exception: exc,
			errorData: exc.errorData,
			stack: exc.stack,
			show
		});

		if (exc.errorData && exc.errorData.errors) {
			exc.errorData.errors.forEach(error => {
				if (error.errorInfo && error.errorInfo.code && show) {
					NotificationManager.error(
						ErrorHandlerLogin(error.message),
						"Error",
						3000,
						null,
						null,
						'error',
						error.errorInfo?.code
					);
				}
				else if (show) {
					NotificationManager.error(
						ErrorHandlerLogin(error.message),
						"Error",
						3000,
						null,
						null,
						'error'
					);
				}
			});
		}
	}
	catch (exc2) {
		console.log("ExceptionManager error: ", exc2);
		LogManager.logToDataDog('error', 'ExceptionManager failed', { originalException: exc, managerException: exc2 });
	}
};

// Add logException method to ExceptionManager for consistency
ExceptionManager.logException = (module, method, exc) => {
	ExceptionManager(exc, module, method, false);
};

export const LogManagerConsole = (key, ...log) => {
	let debug = Boolean(localStorage.getItem("debug")) === true;
	let logKeys = localStorage.getItem("logKeys")
	logKeys = logKeys && JSON.parse(logKeys) || []

	if (debug && (logKeys.length == 0 || logKeys.includes(key))) {
		console.log(`Log ${key}=>`, ...log);
	}
};

export const GetApiErrorLan = (exc) => {

	let errorCode
	if (exc?.errorData?.errors) {
		exc?.errorData?.errors.forEach(x => {
			if (x.errorInfo?.code)
				errorCode = x.errorInfo?.code
		})
	}

	const errors = {
		'1010': 'duplicated_name'
	}

	return errorCode && errors[errorCode] || '';
}
